// Chat composer: form wrapper + inflight strip + targeted-agents
// chips + mention autocomplete popover + textarea + send. State lives
// in the parent (ProjectRoomPage); this component is the render +
// event surface.
//
// Extracted from ProjectRoomPage 2026-04-24 per Reef audit. Accepts
// many props because the composer is genuinely coupled to parent
// state (mention caret, send semantics, inflight derivation). The
// alternative — a composer context — would hide the couplings without
// removing them.

import type { KeyboardEventHandler, Ref, RefObject } from "react";
import type { Agent, MentionOption, ModelSort } from "./types";
import { MentionAutocomplete } from "./MentionAutocomplete";
import { ChatInput } from "./ChatInput";

export interface InflightSeat {
  agentId: number;
  status: string;
}

export function Composer({
  workspaceId,
  composerRef,
  messageInputRef,
  mentionPopupRef,
  mentionListRef,

  newMessage,
  onNewMessageChange,
  onMentionCaretChange,
  onKeyDown,
  onSubmit,
  busy,

  agents,
  activeRoutingCount,
  activeInflight,
  directReplyInFlight,
  targetedAgents,

  mentionMatches,
  mentionActiveIdx,
  onMentionActiveIdxChange,
  workspaceAgentCount,
  modelSort,
  onModelSortChange,
  onMentionPick,
}: {
  workspaceId: number;
  composerRef: Ref<HTMLFormElement>;
  messageInputRef: RefObject<HTMLTextAreaElement | null>;
  mentionPopupRef: RefObject<HTMLDivElement | null>;
  mentionListRef: RefObject<HTMLUListElement | null>;

  newMessage: string;
  onNewMessageChange: (value: string) => void;
  onMentionCaretChange: (caret: number | null) => void;
  onKeyDown: KeyboardEventHandler<HTMLTextAreaElement>;
  onSubmit: (e: React.FormEvent) => void;
  busy: boolean;

  agents: Agent[];
  activeRoutingCount: number;
  activeInflight: InflightSeat[];
  directReplyInFlight: Set<string>;
  targetedAgents: Agent[];

  mentionMatches: MentionOption[];
  mentionActiveIdx: number;
  onMentionActiveIdxChange: (idx: number) => void;
  workspaceAgentCount: number;
  modelSort: ModelSort;
  onModelSortChange: (sort: ModelSort) => void;
  onMentionPick: (option: MentionOption) => void;
}) {
  return (
    <form
      ref={composerRef}
      onSubmit={onSubmit}
      className="absolute bottom-0 left-0 right-0 z-20 bg-transparent px-4 pt-0.5 pb-4"
    >
      {/* Inflight strip — top of the composer form, above the textarea.
       * Reserved h-5 so the composer doesn't jump when seats come
       * and go. */}
      <div
        className="mb-0.5 flex h-4 items-center gap-x-4 px-2 py-px text-[0.625rem] leading-none text-faint"
        data-testid="composer-inflight"
      >
        {activeRoutingCount > 0 && (
          <span className="flex items-center gap-1.5" data-testid="composer-routing">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-60" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-warning" />
            </span>
            <span>
              orchestrator is routing…
              {activeRoutingCount > 1 ? ` (${activeRoutingCount})` : ""}
            </span>
          </span>
        )}
        {activeInflight.map((seat) => {
          const seatAgent = agents.find((a) => a.agent_id === seat.agentId);
          const name = seatAgent?.display_name ?? `agent ${seat.agentId}`;
          const verb = seat.status === "replying" ? "replying" : "thinking";
          return (
            <span key={seat.agentId} className="flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              <span>
                {name} is {verb}…
              </span>
            </span>
          );
        })}
        {Array.from(directReplyInFlight).map((seatKey) => {
          // Direct-model mentions (seat_key contains "/") show the
          // model id directly — the agent row exists but its
          // display_name is a placeholder like "agent 6".
          const isDirectModel = seatKey.includes("/");
          const seatAgent = agents.find((a) => a.seat_key === seatKey);
          const name = isDirectModel
            ? seatKey
            : (seatAgent?.display_name ?? seatKey);
          return (
            <span
              key={`direct-${seatKey}`}
              className="flex items-center gap-1.5"
              data-testid={`direct-reply-inflight-${seatKey}`}
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent" />
              </span>
              <span>{name} is thinking…</span>
            </span>
          );
        })}
      </div>

      {targetedAgents.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2" data-testid="mention-targets">
          {targetedAgents.map((agent) => (
            <span
              key={agent.agent_id}
              className="rounded-md border border-accent px-2 py-1 text-xs text-accent"
            >
              @{agent.seat_key}
            </span>
          ))}
        </div>
      )}

      <MentionAutocomplete
        matches={mentionMatches}
        activeIdx={mentionActiveIdx}
        channelAgentCount={workspaceAgentCount}
        modelSort={modelSort}
        onModelSortChange={onModelSortChange}
        onActiveIdxChange={onMentionActiveIdxChange}
        onPick={onMentionPick}
        onSortBlur={() => messageInputRef.current?.focus()}
        popupRef={mentionPopupRef}
        listRef={mentionListRef}
      />

      <ChatInput
        value={newMessage}
        onChange={(value, caret) => {
          onNewMessageChange(value);
          onMentionCaretChange(caret);
        }}
        onKeyDown={onKeyDown}
        onBlur={(e) => {
          const next = e.relatedTarget as Node | null;
          if (next && mentionPopupRef.current?.contains(next)) return;
          window.setTimeout(() => onMentionCaretChange(null), 120);
        }}
        inputRef={messageInputRef}
        placeholder={`Message workspace #${workspaceId}`}
        busy={busy}
        testId="message-input"
      />
    </form>
  );
}
